/**
 * Offisiell regattaliste (/regattaer) — offentlig, uten innlogging.
 *
 * Klubb velges øverst (?klubb=<slug>, huskes på enheten). Viser regattaer de
 * neste 14 dagene (inkl. pågående, utsatte og avlyste) og ferdige regattaer med
 * lenke til resultater og reprise.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Forening, type RegattaListItem } from '../lib/api.ts';
import { SiteLayout, SEA, SAND } from '../components/SiteLayout.tsx';

const TZ = 'Europe/Oslo';
const DAY_MS = 24 * 3600 * 1000;
const CLUB_KEY = 'rc_forening';

/** YYYY-MM-DD i norsk tid. */
function osloDay(d: Date) {
  return d.toLocaleDateString('sv-SE', { timeZone: TZ });
}

function noPart(d: Date, opts: Intl.DateTimeFormatOptions) {
  return d.toLocaleDateString('no-NO', { ...opts, timeZone: TZ }).replace('.', '');
}

type Status = { bar: string; badge?: string; badgeCls?: string; when: string; live: boolean };

function statusOf(r: RegattaListItem, now: number): Status {
  const start = new Date(r.startTime);
  const time = start.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
  if (r.status === 'cancelled') {
    return { bar: '#ef4444', badge: 'Avlyst', badgeCls: 'bg-red-900/50 text-red-300', when: `Var satt til kl. ${time}`, live: false };
  }
  if (r.status === 'postponed') {
    return { bar: '#f59e0b', badge: 'Utsatt', badgeCls: 'bg-amber-900/50 text-amber-300', when: 'Venter på ny starttid', live: false };
  }
  if (r.status === 'started' || start.getTime() <= now) {
    return { bar: '#10b981', badge: 'Pågår', badgeCls: 'bg-emerald-900/50 text-emerald-300', when: `Startet kl. ${time}`, live: true };
  }
  const days = Math.round((Date.parse(osloDay(start)) - Date.parse(osloDay(new Date(now)))) / DAY_MS);
  const when =
    days === 0 ? `I dag kl. ${time}` : days === 1 ? `I morgen kl. ${time}` : `Om ${days} dager · kl. ${time}`;
  return { bar: SEA, when, live: false };
}

function DateTile({ date }: { date: Date }) {
  return (
    <div className="w-14 shrink-0 self-start text-center rounded-lg bg-slate-950/70 border border-slate-800 py-1.5">
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: SAND }}>
        {noPart(date, { weekday: 'short' })}
      </div>
      <div className="text-2xl font-extrabold leading-none text-white mt-0.5">
        {noPart(date, { day: 'numeric' })}
      </div>
      <div className="text-[10px] uppercase text-slate-400 mt-0.5">{noPart(date, { month: 'short' })}</div>
    </div>
  );
}

function RegattaCard({ r, now, showClub }: { r: RegattaListItem; now: number; showClub: boolean }) {
  const st = statusOf(r, now);
  const open = r.status === 'scheduled' || r.status === 'postponed';
  const deadlinePassed = !!r.registrationDeadline && new Date(r.registrationDeadline).getTime() <= now;
  const regOpen = !!r.registrationUrl && open && !deadlinePassed;
  const sub = [r.venue, showClub ? r.foreningName : null].filter(Boolean).join(' · ');

  return (
    <li className="relative bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: st.bar }} />
      <div className="p-4 pl-5 flex gap-4">
        <DateTile date={new Date(r.startTime)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-white leading-snug">{r.name}</h3>
            {st.badge && (
              <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded ${st.badgeCls}`}>
                {st.live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                {st.badge}
              </span>
            )}
          </div>
          {sub && <div className="text-xs text-slate-400 mt-0.5 truncate">{sub}</div>}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-slate-300">{st.when}</span>
            <Link to={`/r/${r.slug}/pameldte`} className="text-slate-400 hover:text-white">
              ⛵ {r.entryCount} påmeldt
            </Link>
            {r.startMode === 'manual' && <span className="text-slate-400">⏱ Egen startklokke</span>}
            {r.startMode === 'committee' && <span className="text-slate-400">⏱ Komiteen starter klokka</span>}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {r.status !== 'cancelled' && (
              <a
                href={`/kart/${r.slug}`}
                target="rc-kart"
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:brightness-110 transition"
                style={{ background: SEA }}
              >
                🗺️ Følg live
              </a>
            )}
            <Link
              to={`/r/${r.slug}`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-700 text-slate-200 hover:bg-slate-800"
            >
              ⏱ Regattaside
            </Link>
            {regOpen && (
              <a
                href={r.registrationUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:brightness-110 transition"
                style={{ background: SAND }}
              >
                Påmelding ↗
              </a>
            )}
            {!!r.registrationUrl && open && deadlinePassed && (
              <span className="px-1 text-xs text-slate-500">Påmeldingen er stengt</span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ResultRow({ r, showClub }: { r: RegattaListItem; showClub: boolean }) {
  const date = new Date(r.startTime).toLocaleDateString('no-NO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: TZ,
  });
  const sub = [date, r.venue, showClub ? r.foreningName : null, `${r.entryCount} båter`]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="flex items-center gap-3 py-3">
      <span className="text-lg shrink-0" aria-hidden="true">
        🏁
      </span>
      <div className="min-w-0 flex-1">
        <Link to={`/r/${r.slug}/resultater`} className="font-semibold text-slate-100 hover:text-white truncate block">
          {r.name}
        </Link>
        <div className="text-xs text-slate-500 truncate">{sub}</div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1 text-xs">
        <Link to={`/r/${r.slug}/resultater`} className="font-semibold text-sky-400 hover:text-sky-300">
          Resultater
        </Link>
        <a href={`/kart/${r.slug}/reprise`} target="rc-kart" className="text-slate-400 hover:text-slate-200">
          ⏮ Reprise
        </a>
      </div>
    </li>
  );
}

function ClubChips({
  clubs,
  value,
  onChange,
}: {
  clubs: Forening[];
  value: string;
  onChange: (slug: string) => void;
}) {
  const cls = (active: boolean) =>
    `inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
      active
        ? 'text-white border-transparent shadow-lg shadow-black/30'
        : 'bg-slate-950/40 border-slate-600/70 text-slate-200 hover:border-slate-400 hover:text-white'
    }`;
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Velg klubb">
      <button
        type="button"
        onClick={() => onChange('')}
        className={cls(!value)}
        style={!value ? { background: SEA } : undefined}
        aria-pressed={!value}
      >
        Alle klubber
      </button>
      {clubs.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onChange(f.slug)}
          className={cls(value === f.slug)}
          style={value === f.slug ? { background: SEA } : undefined}
          aria-pressed={value === f.slug}
        >
          {f.hasLogo && (
            <img
              src={`/api/foreninger/${f.slug}/logo`}
              alt=""
              className="w-5 h-5 rounded-full bg-white p-0.5 object-contain"
            />
          )}
          {f.name}
        </button>
      ))}
    </div>
  );
}

function SectionHead({ title, count }: { title: string; count?: number }) {
  return (
    <h2
      className="flex items-center gap-3 text-xs font-bold tracking-[0.14em] uppercase mb-4"
      style={{ color: SAND }}
    >
      {title}
      {count != null && <span className="text-slate-500 font-semibold tracking-normal">{count}</span>}
      <span className="flex-1 h-px bg-slate-800" />
    </h2>
  );
}

function Empty({ children }: { children: string }) {
  return (
    <p className="text-sm text-slate-400 bg-slate-900/40 border border-dashed border-slate-800 rounded-xl px-4 py-6 text-center">
      {children}
    </p>
  );
}

export function RegattaerPage() {
  const [params, setParams] = useSearchParams();
  const [showAll, setShowAll] = useState(false);
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });
  const clubs = foreninger.data ?? [];

  let stored = '';
  try {
    stored = localStorage.getItem(CLUB_KEY) ?? '';
  } catch {
    /* privat modus o.l. */
  }
  const requested = params.get('klubb') ?? stored;
  // Ukjent eller utdatert klubb → alle klubber.
  const klubb = foreninger.isSuccess && !clubs.some((f) => f.slug === requested) ? '' : requested;
  const club = clubs.find((f) => f.slug === klubb);

  function choose(slug: string) {
    try {
      if (slug) localStorage.setItem(CLUB_KEY, slug);
      else localStorage.removeItem(CLUB_KEY);
    } catch {
      /* ignorer */
    }
    setShowAll(false);
    setParams(slug ? { klubb: slug } : {}, { replace: true });
  }

  const list = useQuery({
    queryKey: ['regattaer', klubb],
    queryFn: () => api.regattaer(klubb || undefined),
    enabled: !foreninger.isPending,
    refetchInterval: 60_000,
  });

  const now = Date.now();
  const upcoming = list.data?.upcoming ?? [];
  const finished = list.data?.finished ?? [];
  const visibleFinished = showAll ? finished : finished.slice(0, 10);
  const showClub = !klubb;

  return (
    <SiteLayout>
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg,#0a2740 0%,#103a5c 65%,#0b2a42 100%)' }}
      >
        <svg
          className="absolute inset-x-0 bottom-0 w-full h-14"
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0 30 q120 -16 240 0 t240 0 t240 0 t240 0 t240 0 t240 0 V60 H0 Z"
            fill="#020617"
            opacity="0.55"
          />
          <path
            d="M0 42 q90 -10 180 0 t180 0 t180 0 t180 0 t180 0 t180 0 t180 0 t180 0 V60 H0 Z"
            fill="#020617"
          />
        </svg>
        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-14 sm:pt-12">
          <span
            className="inline-flex items-center gap-2.5 text-xs font-bold tracking-[0.14em] uppercase"
            style={{ color: SAND }}
          >
            <span className="w-6 h-0.5 rounded" style={{ background: SAND }} />
            Offisiell oversikt
          </span>
          <h1 className="mt-3 font-extrabold tracking-tight text-white text-3xl sm:text-4xl">
            Regattaer og resultater
          </h1>
          <p className="mt-2 text-slate-300 max-w-[60ch]">
            {club ? `${club.name}: kommende` : 'Kommende'} regattaer de neste 14 dagene, og resultater
            fra gjennomførte seilaser.
          </p>
          <div className="mt-6">
            <ClubChips clubs={clubs} value={klubb} onChange={choose} />
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-12">
        {list.isError ? (
          <Empty>Klarte ikke å hente regattaene. Prøv igjen om litt.</Empty>
        ) : (
          <>
            <section>
              <SectionHead title="Neste 14 dager" count={list.data ? upcoming.length : undefined} />
              {!list.data ? (
                <p className="text-sm text-slate-500">Laster regattaer…</p>
              ) : upcoming.length === 0 ? (
                <Empty>Ingen regattaer de neste 14 dagene.</Empty>
              ) : (
                <ul className="grid gap-4 md:grid-cols-2">
                  {upcoming.map((r) => (
                    <RegattaCard key={r.id} r={r} now={now} showClub={showClub} />
                  ))}
                </ul>
              )}
            </section>

            <section>
              <SectionHead title="Resultater" count={list.data ? finished.length : undefined} />
              {!list.data ? null : finished.length === 0 ? (
                <Empty>Ingen ferdige regattaer ennå.</Empty>
              ) : (
                <>
                  <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-4">
                    <ul className="divide-y divide-slate-800/70">
                      {visibleFinished.map((r) => (
                        <ResultRow key={r.id} r={r} showClub={showClub} />
                      ))}
                    </ul>
                  </div>
                  {finished.length > 10 && (
                    <button
                      onClick={() => setShowAll((v) => !v)}
                      className="mt-3 text-sm text-sky-400 hover:text-sky-300"
                    >
                      {showAll ? '▴ Vis færre' : `▾ Vis alle (${finished.length})`}
                    </button>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </main>
    </SiteLayout>
  );
}
